import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { defaultDomains } from '../mock';
import { Plus, X, RotateCcw, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import WheelChart from '../components/WheelChart';

const CustomizePage = () => {
  const [domains, setDomains] = useState(() => {
    const saved = localStorage.getItem('customDomains');
    if (saved) {
      const savedDomains = JSON.parse(saved);
      // Ensure all domains have baseDomain property
      return savedDomains.map(domain => ({
        ...domain,
        baseDomain: domain.baseDomain || domain.name, // Use existing name as baseDomain if not set
        customName: domain.customName || domain.name
      }));
    }
    // Initialize with default domains
    return defaultDomains.map(domain => ({
      ...domain,
      baseDomain: domain.name,
      customName: domain.name
    }));
  });
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem('customDomains', JSON.stringify(domains));
  }, [domains]);

  const handleNameChange = (id, newName) => {
    const updated = domains.map((d) =>
      d.id === id ? { ...d, customName: newName } : d
    );
    setDomains(updated);
  };

  const handleBaseDomainChange = (id, baseDomainName) => {
    const baseDomain = defaultDomains.find(d => d.name === baseDomainName);
    const updated = domains.map((d) =>
      d.id === id ? { ...d, baseDomain: baseDomainName, questions: baseDomain?.questions || [] } : d
    );
    setDomains(updated);
  };

  const handleColorChange = (id, newColor) => {
    const updated = domains.map((d) =>
      d.id === id ? { ...d, color: newColor } : d
    );
    setDomains(updated);
  };

  const handleAddDomain = () => {
    if (domains.length >= 16) {
      toast({
        title: 'Maximum reached',
        description: 'You can have up to 16 domains in your wheel.',
        variant: 'destructive',
      });
      return;
    }

    const colors = ['#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722', '#E91E63', '#9C27B0'];
    const defaultBaseDomain = defaultDomains[0]; // Use first default domain as base
    const newDomain = {
      id: Date.now(),
      baseDomain: defaultBaseDomain.name,
      customName: `Domain ${domains.length + 1}`,
      color: colors[domains.length % colors.length],
      score: 0,
      questions: defaultBaseDomain.questions
    };
    setDomains([...domains, newDomain]);
  };

  const handleRemoveDomain = (id) => {
    if (domains.length <= 4) {
      toast({
        title: 'Minimum required',
        description: 'You need at least 4 domains in your wheel.',
        variant: 'destructive',
      });
      return;
    }
    setDomains(domains.filter((d) => d.id !== id));
  };

  const handleReset = () => {
    const resetDomains = defaultDomains.map(domain => ({
      ...domain,
      baseDomain: domain.name,
      customName: domain.name
    }));
    setDomains(resetDomains);
    toast({
      title: 'Reset complete',
      description: 'Your wheel has been reset to default domains.',
    });
  };

  const handleSave = () => {
    // Update assessment with new domains
    localStorage.setItem('wheelAssessment', JSON.stringify(domains));
    toast({
      title: 'Saved successfully!',
      description: 'Your custom wheel has been saved.',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Customize Your Wheel
          </h1>
          <p className="text-lg text-gray-600 mb-4">
            Personalize the life domains to match what matters most to you
          </p>
          <Link to="/customize-guide">
            <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
              📚 View Customization Guide & Color Palettes
            </Button>
          </Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <Card className="p-6 bg-white shadow-xl mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Life Domains</h2>
                <div className="flex gap-2">
                  <Button
                    onClick={handleReset}
                    variant="outline"
                    size="sm"
                    className="border-gray-300"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset
                  </Button>
                  <Button
                    onClick={handleAddDomain}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {domains.map((domain) => (
                  <div
                    key={domain.id}
                    className="p-4 border-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    style={{ borderColor: domain.color + '40' }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 space-y-3">
                        <div>
                          <Label className="text-xs text-gray-600 mb-1">Base Domain (Questions Source)</Label>
                          <select
                            value={domain.baseDomain}
                            onChange={(e) => handleBaseDomainChange(domain.id, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            {defaultDomains.map((defaultDomain) => (
                              <option key={defaultDomain.id} value={defaultDomain.name}>
                                {defaultDomain.name}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            This determines which questions appear in the assessment
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 mb-1">Custom Domain Name (Display)</Label>
                          <Input
                            value={domain.customName}
                            onChange={(e) => handleNameChange(domain.id, e.target.value)}
                            className="font-medium"
                            placeholder="Enter your custom name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 mb-1">Color</Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={domain.color}
                              onChange={(e) => handleColorChange(domain.id, e.target.value)}
                              className="w-12 h-10 rounded border-2 border-gray-300 cursor-pointer"
                            />
                            <Input
                              value={domain.color}
                              onChange={(e) => handleColorChange(domain.id, e.target.value)}
                              className="font-mono text-sm"
                            />
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleRemoveDomain(domain.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-1"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Button
              onClick={handleSave}
              className="w-full bg-green-600 hover:bg-green-700 text-white shadow-lg"
              size="lg"
            >
              <Save className="w-5 h-5 mr-2" />
              Save & Apply to Assessment
            </Button>
          </div>

          <div>
            <Card className="p-6 bg-white shadow-xl sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
                Live Preview
              </h2>
              <WheelChart domains={domains} editable={false} />
              <p className="text-sm text-gray-600 text-center mt-6">
                This is how your customized wheel will look
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizePage;
